import Notification from "@/types/Notification.interface"
import IListIcon, { IListIconType } from '@/types/components/IListIcon.interface'
import Button from "@/types/components/Button.interface"

export default {
    data() {
        return {
            destinations: {
                autoresponders: '/auto-responders',
                billing: '/billing/subscription',
                broadcasts: '/broadcasts',
                chatbot: '/chatbots',
                contacts: '/contacts',
                forms: '/forms',
                invoices: '/invoices',
                messages: '/inbox',
                users: '/teammates'
            },
            displays: {
                autoresponder: 'Go to Autoresponder',
                autoresponders: 'Go to Autoresponders',
                billing: 'Go to Subscription',
                broadcast: 'Go to Broadcast',
                broadcasts: 'Go to Broadcasts',
                chatbot: 'Go to Chatbots',
                contacts: 'Go to Contacts',
                conversation: 'Go to Conversation',
                csv: 'Go to CSV Import/Export',
                csvExport: 'Download CSV',
                csvImport: 'View Import Report',
                customFields: 'Go to Custom Fields',
                forms: 'Go to Forms',
                invoices: 'Go to Invoices',
                messages: 'Go to Inbox',
                users: 'Go to Teammates'
            }
        }
    },
    methods: {
        //  This is the handler for a notification with 'exec = downloadCsv'
        async downloadCsv(n: Notification) {
            //  Check that the notification action is 'downloadCsv;
            const isDownloadCsv = n.data?.action?.exec === 'downloadCsv'

            //  If the notification action is NOT 'downloadCsv' throw error
            if (!isDownloadCsv) throw 'Notification does not contain an executable action: downloadCsv'

            //  Else, get the csv down link from the notification action
            const link = n.data.action.params

            //  Download the csv
            const success = await this.$store.dispatch('csvDownload', link)

            //  If the download was not successful, pop failure message
            if (!success) {
                return this.$store.dispatch('dialogOpen', {
                    type: 'MESSAGE',
                    title: 'CSV Download Error',
                    text: `There was a problem while downloading the CSV. This is often due to an expired link. Please try your CSV export again.`
                })
            }

            //  Else, pop success message
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'CSV Downloaded',
                text: 'CSV successfully downloaded.'
            })
        },
        //  This is the handler for a notification with 'exec = importShowReport'
        importShowReport(n: Notification) {
            //  Check that the notification action is 'importShowReport';
            const isShowReport = n.data?.action?.exec === 'importShowReport'

            //  If the notification action is NOT 'importShowReport' throw error
            if (!isShowReport) throw 'Notification does not contain executable action: importShowReport'

            //  Else, get the import report
            const report = n.data.action.params

            //  Show the import report
            console.error('TODO: show import report')
        },
        //  Parse the destination related to a notification
        notificationButton(n: Notification): Button {
            //  Check if the notification has a route set
            const route = n.data?.route

            //  If an explicit route is set, use it
            if (route) {
                let text = 'Go To'

                //  If route contains inbox?channel, 'Go to Conversation'
                if (route.includes('inbox?channel')) text = this.displays.conversation

                //  if route contains auto-responders?id, 'Go to Autoresponder'
                if (route.includes('auto-responders?id')) text = this.displays.autoresponder

                //  if route contains custom-fields, 'Go to Custom Fields'
                if (route.includes('custom-fields')) text = this.displays.customFields

                //  if route contains csv, 'Go To Import/Export'
                if (route.includes('csv')) text = this.displays.csv

                //  If the route contains billing, 'Go to subscription'
                if (route.includes('billing/subscription')) text = this.displays.billing

                //  If the route contains broadcasts/, 'Go to broadcast'
                if (route.includes('broadcasts/')) text = this.displays.broadcast

                //  Return the 'route' button
                return {
                    text: text,
                    callback: () => {
                        //  Get the current path
                        const path = this.$route.fullPath

                        //  If the path is NOT the same as the route, then navigate
                        if (path !== route) this.$router.push(route)

                        //  If the notifiaction is 'unread' dismiss it
                        if (!n.read_at) this.$store.dispatch('notificationsDismiss', [n.id])

                        //  If a close function exists, use it (ie the template popup is open, so close it)
                        this.$store.dispatch('dialogClose', 'APP')
                    }
                }
            }

            //  Else, check if the notification has an action set
            const action = n.data.action

            //  If the notification has an action, handle it
            if (action?.exec) {

                /**
                 * Currently 'exec' is used in only two notifications. 'CSV export
                 * completed' and 'CSV import completed'. Functions named by 'exec'
                 * are located in this mixin. ~wcv
                 */

                //  Define default display text
                let text = 'Run executable'

                //  Check if this is the csvExport notification
                const isCsvExport = n.data.text.includes('Your CSV export of');

                //  If the executable is 'csvExport' use that display text
                if (isCsvExport) text = this.displays.csvExport

                //  Check if this the csvImport notification
                const isCsvImport = n.data.text.includes('Your CSV upload of');

                //  IF the executable is 'csvImport' use that display text
                if (isCsvImport) text = this.displays.csvImport

                //  Return the 'exec' button
                return {
                    text: text,
                    callback: () => {
                        //  Run the action executable
                        this[action.exec](n)

                        //  If the notifiaction is 'unread' dismiss it
                        if (!n.read_at) this.$store.dispatch('notificationsDismiss', [n.id])

                        //  Close the APP dialog (this handles the case when the notification is hosted in the unread notifications popup)
                        this.$store.dispatch('dialogClose', 'APP')
                    }
                }
            }

            //  Else, check if the notification has a category
            const category = n.data.category || n.category

            //  If the notification has a category, select destination based on category
            if (category && category != 'system') {

                //  Get the display text and destination of the category
                const text = this.displays[category]
                const dest = this.destinations[category]

                //  Return the 'category' button
                return {
                    text: text,
                    callback: () => {
                        //  Navigate to the category destination
                        this.$router.push(dest)

                        //  If the notifiaction is 'unread' dismiss it
                        if (!n.read_at) this.$store.dispatch('notificationsDismiss', [n.id])

                        //  If a close function exists, use it (ie the template popup is open, so close it)
                        this.$store.dispatch('dialogClose', 'APP')
                    }
                }
            }

            //  Else, we were not able to parse a destination, so return null
            return null
        },
        //  Get the icon shown on the left side of the notification IList tile
        notificationIcon(notification: Notification): IListIcon {
            //  We always use the notification's icon
            const icon = notification.data.icon

            //  The color depends on if this has been read or not
            const color = notification.read_at ? 'grey' : 'primary'

            //  Create our IListIcon
            const listIcon: IListIcon = {
                type: icon === 'admin' ? IListIconType.admin : IListIconType.icon,
                content: icon,
                color: color
            }

            //  Check if the notification is unread
            const isUnread = !notification.read_at

            //  If the notification is unread, add a dot badge
            if (isUnread) listIcon.badge = { color: this.$colors.ruby, dot: true }

            //  Return our IListIcon
            return listIcon
        }
    }
}
